/**
 * Local, deterministic image validation (README 3.3). No AI involved.
 *
 * Checks: file type and size, resolution, mean brightness and sharpness
 * (variance of the Laplacian). Sharpness can only warn, never reject.
 * The visual checks returned by the analysis endpoint are merged later.
 */

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];
export const MAX_FILE_BYTES = 12 * 1024 * 1024;

const MIN_SIDE_FAILED = 600;
const MIN_SIDE_WARNING = 900;
const BRIGHTNESS_WARNING_RANGE = [40, 225];
const BRIGHTNESS_FAILED_RANGE = [20, 240];
// Heuristic threshold for a 512 px grayscale copy; tuned to flag clearly blurred photos only.
const SHARPNESS_MIN_VARIANCE = 40;
const SAMPLE_MAX_SIZE = 512;

const MESSAGES = {
  INVALID_TYPE: "Formato não aceito. Use uma foto JPEG, PNG ou WebP.",
  FILE_TOO_LARGE: "O arquivo tem mais de 12 MB. Use uma foto menor.",
  RESOLUTION_TOO_LOW: "A resolução da foto é baixa demais para a medição. Use uma foto com pelo menos 600 px no menor lado.",
  RESOLUTION_LOW: "A resolução da foto é baixa. A medição pode ficar menos precisa.",
  TOO_DARK: "A foto está escura demais. Procure um local mais iluminado.",
  TOO_BRIGHT: "A foto está clara demais. Evite luz direta ou reflexos.",
  SLIGHTLY_DARK: "A foto está um pouco escura. Se possível, melhore a iluminação.",
  SLIGHTLY_BRIGHT: "A foto está um pouco clara. Se possível, evite luz direta.",
  BLURRY: "A foto parece desfocada. Se possível, segure o celular firme e tire outra.",
  ANALYSIS_FAILED: "Não foi possível verificar a iluminação e a nitidez desta foto.",
};

// `check` ties the issue to its entry in `checks` so the UI can show it inline.
const issue = (code, check) => ({ code, message: MESSAGES[code], check });

const loadSource = async (display) => {
  if (typeof createImageBitmap === "function" && display.blob) {
    try {
      const bitmap = await createImageBitmap(display.blob);
      return { source: bitmap, width: bitmap.width, height: bitmap.height, release: () => bitmap.close && bitmap.close() };
    } catch (e) {
      // Fall back to <img> below.
    }
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ source: image, width: image.naturalWidth, height: image.naturalHeight, release: null });
    image.onerror = () => reject(new Error("IMAGE_DECODE_FAILED"));
    image.src = display.url;
  });
};

/** Grayscale luminance (0..255) of a downscaled copy of the display image. */
const readGrayscale = async (display) => {
  const loaded = await loadSource(display);

  try {
    const scale = Math.min(1, SAMPLE_MAX_SIZE / Math.max(loaded.width, loaded.height));
    const width = Math.max(1, Math.round(loaded.width * scale));
    const height = Math.max(1, Math.round(loaded.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    context.drawImage(loaded.source, 0, 0, width, height);

    const { data } = context.getImageData(0, 0, width, height);
    const gray = new Float32Array(width * height);
    for (let i = 0, p = 0; i < gray.length; i++, p += 4) {
      gray[i] = 0.2126 * data[p] + 0.7152 * data[p + 1] + 0.0722 * data[p + 2];
    }

    return { gray, width, height };
  } finally {
    if (loaded.release) loaded.release();
  }
};

const meanOf = (values) => {
  let sum = 0;
  for (let i = 0; i < values.length; i++) sum += values[i];
  return values.length ? sum / values.length : 0;
};

/** Variance of the 4-neighbour Laplacian; low values mean a blurred image. */
const laplacianVariance = ({ gray, width, height }) => {
  if (width < 3 || height < 3) return 0;

  let sum = 0;
  let sumSquares = 0;
  let count = 0;

  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const value = gray[i - width] + gray[i + width] + gray[i - 1] + gray[i + 1] - 4 * gray[i];
      sum += value;
      sumSquares += value * value;
      count++;
    }
  }

  const mean = sum / count;
  return sumSquares / count - mean * mean;
};

const checkFile = (file, problems) => {
  const type = (file && file.type) || "";
  if (type && ACCEPTED_IMAGE_TYPES.indexOf(type) === -1) {
    problems.push(issue("INVALID_TYPE", "file"));
    return "failed";
  }
  if (file && file.size > MAX_FILE_BYTES) {
    problems.push(issue("FILE_TOO_LARGE", "file"));
    return "failed";
  }
  return "passed";
};

const checkResolution = (display, warnings, problems) => {
  const minSide = Math.min(display.width, display.height);
  if (minSide < MIN_SIDE_FAILED) {
    problems.push(issue("RESOLUTION_TOO_LOW", "resolution"));
    return "failed";
  }
  if (minSide < MIN_SIDE_WARNING) {
    warnings.push(issue("RESOLUTION_LOW", "resolution"));
    return "warning";
  }
  return "passed";
};

const checkBrightness = (mean, warnings, problems) => {
  if (mean < BRIGHTNESS_FAILED_RANGE[0]) {
    problems.push(issue("TOO_DARK", "brightness"));
    return "failed";
  }
  if (mean > BRIGHTNESS_FAILED_RANGE[1]) {
    problems.push(issue("TOO_BRIGHT", "brightness"));
    return "failed";
  }
  if (mean < BRIGHTNESS_WARNING_RANGE[0]) {
    warnings.push(issue("SLIGHTLY_DARK", "brightness"));
    return "warning";
  }
  if (mean > BRIGHTNESS_WARNING_RANGE[1]) {
    warnings.push(issue("SLIGHTLY_BRIGHT", "brightness"));
    return "warning";
  }
  return "passed";
};

/**
 * @param {File|Blob} file Original file (type and size are checked on it).
 * @param {{ blob: Blob, url: string, width: number, height: number }} display Prepared display copy.
 * @returns {Promise<{ status: string, checks: object, warnings: Array, problems: Array }>}
 */
export const validateCranialImage = async (file, display) => {
  const warnings = [];
  const problems = [];
  const checks = {
    file: checkFile(file, problems),
    resolution: checkResolution(display, warnings, problems),
    brightness: "passed",
    sharpness: "passed",
  };

  try {
    const sample = await readGrayscale(display);
    checks.brightness = checkBrightness(meanOf(sample.gray), warnings, problems);

    if (laplacianVariance(sample) < SHARPNESS_MIN_VARIANCE) {
      warnings.push(issue("BLURRY", "sharpness"));
      checks.sharpness = "warning";
    }
  } catch (e) {
    // Pixel analysis is best effort: never block the flow because of it.
    warnings.push(issue("ANALYSIS_FAILED", "brightness"));
    checks.brightness = "warning";
    checks.sharpness = "warning";
  }

  let status = "approved";
  if (problems.length > 0) status = "rejected";
  else if (warnings.length > 0) status = "approved_with_warnings";

  return { status, checks, warnings, problems };
};
